import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { clsx } from 'clsx'

import {
  Button,
  ExpandIcon,
  ExternalLinkIcon,
  Flex,
  IconButton,
  Tooltip,
} from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'
import { normalizeNetworkName } from '~publish/helpers/channels'
import type {
  CommentsPostCard_Post,
  CommentsPostCard_PostData,
} from '~publish/pages/Community/fragments/CommentsPostCard_Post'

import { PostMiniature } from './PostMiniature'
import { PostMiniatureReels } from './PostMiniatureReels'
import { usePostMiniatureActions } from './usePostMiniatureActions'
import { isMediaFirstPost } from './utils'

import styles from './PostMiniatureButton.module.css'

import type { JSX } from 'react'

type PostMiniatureViewProps = {
  post: CommentsPostCard_PostData
  showChannel: boolean
  isListView?: boolean
  mediaFirst?: boolean
  className?: string
  actions?: ReactNode
}

export const PostMiniatureView = ({
  post,
  showChannel,
  isListView = false,
  mediaFirst = isMediaFirstPost(post),
  className,
  actions,
}: PostMiniatureViewProps): JSX.Element => {
  const isMediaFirstVariant = mediaFirst
  // All posts with media use the text+assets grid layout (media-only posts
  // show a "no caption" placeholder), so the action buttons always need to
  // offset past the asset column.
  const hasTextAndMedia = (post.assets?.length ?? 0) > 0

  return (
    <div
      className={clsx(styles.miniatureContainer, className)}
      data-has-text-and-media={!isMediaFirstVariant && hasTextAndMedia}
      data-variant={isMediaFirstVariant && !isListView ? 'reels' : 'default'}
    >
      {isMediaFirstVariant && !isListView ? (
        <PostMiniatureReels post={post} showChannel={showChannel} />
      ) : (
        <PostMiniature
          post={post}
          condensed={false}
          showChannel={showChannel}
        />
      )}
      {actions}
    </div>
  )
}

type PostMiniatureButtonProps = {
  post: FragmentOf<typeof CommentsPostCard_Post>
  className?: string
}

export const PostMiniatureButton = ({
  post: maskedPost,
  className,
}: PostMiniatureButtonProps): JSX.Element => {
  const {
    post,
    showChannel,
    isListView,
    showAllCommentsRoute,
    handleShowAllComments,
    detailsDialogLocation,
  } = usePostMiniatureActions(maskedPost)

  const openPostLabel = `Open post on ${normalizeNetworkName(
    post.channelService,
  )}`

  return (
    <PostMiniatureView
      post={post}
      showChannel={showChannel}
      isListView={isListView}
      className={className}
      actions={
        <Flex
          role="toolbar"
          aria-label="Post actions"
          gap="2xs"
          align="center"
          className={styles.iconButtonsContainer}
        >
          {isListView && showAllCommentsRoute && (
            <Tooltip content="Show all comments for this post">
              <Button
                variant="secondary"
                size="small"
                className={styles.showAllCommentsButton}
                onClick={handleShowAllComments}
              >
                Show All Comments
              </Button>
            </Tooltip>
          )}
          <IconButton
            as={Link}
            to={detailsDialogLocation}
            label="Post details"
            size="small"
            variant="secondary"
            className={styles.iconButton}
            tooltip="Post details"
          >
            <ExpandIcon />
          </IconButton>
          {post.externalLink && (
            <IconButton
              as="a"
              href={post.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              label={openPostLabel}
              size="small"
              variant="secondary"
              className={styles.iconButton}
              tooltip={openPostLabel}
            >
              <ExternalLinkIcon />
            </IconButton>
          )}
        </Flex>
      }
    />
  )
}
